import { NumberInput, type NumberInputProps } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { MRT_Header, MRT_RowData, MRT_TableInstance } from 'mantine-react-table'
import { type MouseEvent, useEffect, useRef, useState } from 'react'

interface Props<TData extends MRT_RowData> extends NumberInputProps {
  header: MRT_Header<TData>
  rangeFilterIndex: number
  table: MRT_TableInstance<TData>
}

export function TableFilterInput<TData extends MRT_RowData>({
  header,
  rangeFilterIndex,
  table,
  ...rest
}: Props<TData>) {
  const {
    options: { mantineFilterTextInputProps },
    refs: { filterInputRefs },
  } = table
  const { column } = header
  const { columnDef } = column

  const arg = { column, rangeFilterIndex, table }
  const inputProps = {
    ...parseFromValuesOrFunc(mantineFilterTextInputProps, arg),
    ...parseFromValuesOrFunc(columnDef.mantineFilterTextInputProps, arg),
    ...rest,
  }

  const isMounted = useRef(false)

  const [filterValue, setFilterValue] = useState<any>(
    () => (column.getFilterValue() as [number, number, number])?.[rangeFilterIndex] || ''
  )

  const [debouncedFilterValue] = useDebouncedValue(filterValue, 400)

  // send debounced filterValue to table instance
  useEffect(() => {
    if (!isMounted.current) return
    column.setFilterValue((old: [number, number, number]) => {
      const newFilterValues = Array.isArray(old) ? old : ['', '', '']
      newFilterValues[rangeFilterIndex] = debouncedFilterValue as number
      return newFilterValues
    })
  }, [debouncedFilterValue])

  // receive table filter value and set it to local state
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    const tableFilterValue = column.getFilterValue()
    if (tableFilterValue === undefined) {
      handleClear()
    } else {
      setFilterValue(((tableFilterValue ?? ['', '', '']) as [number, number, number])[rangeFilterIndex])
    }
  }, [column.getFilterValue()])

  const handleClear = () => {
    setFilterValue('')
    column.setFilterValue((old: [number | undefined, number | undefined, number | undefined]) => {
      const newFilterValues = Array.isArray(old) ? old : ['', '', '']
      newFilterValues[rangeFilterIndex] = undefined
      return newFilterValues
    })
  }

  return (
    <NumberInput
      onClick={(event: MouseEvent<HTMLInputElement>) => event.stopPropagation()}
      value={filterValue}
      onChange={(e) => setFilterValue(e)}
      {...rest}
      mt={0}
      ref={(node) => {
        if (node) {
          filterInputRefs.current[`${column.id}-${rangeFilterIndex ?? 0}`] = node
          if (inputProps.ref) {
            inputProps.ref.current = node
          }
        }
      }}
    />
  )
}

function parseFromValuesOrFunc<T, U>(fn: ((arg: U) => T) | T | undefined, arg: U): T | undefined {
  return fn instanceof Function ? fn(arg) : fn
}
